import {all, call, delay, put, select, takeLatest} from 'redux-saga/effects';
import {ActionType} from 'typesafe-actions';
import {AxiosResponse} from 'axios';
import qs from 'qs';
import * as actions from '../../actions';
import {APIErrorInterface, ReduxState} from '../../types';
import {api} from '../../services/axios';
import {Account} from '../../../services/endpoints';
import {getRoute, Routes} from '../../../routes/routes';
import history from '../../../history';
import JSONFormData from '../../../utils/JSONFormData';
import {showToast} from '../../../utils/showToast';
import i18n from '../../../services/i18n';
import {AccountListItem, TransactionItem} from '../../actions/accounts/payloads';
import {getConfigData, getCountriesList, getSubdivisionsData, getTimeZonesList} from '../generic/saga';
import {compareObjectsAndReturnDifferencesInValues} from '../../../utils/compareObjects';
import {ConfigData, PasswordComplexityRules} from '../../types/ConfigData';
import {CreateAccountFaultCode, CreateAccountFaultString} from '../../types/Account';
import {isMaxOfferedQuantityFault} from '../../types/faults';
import {EXTENSION_NOT_FOUND_BY_ID_API_ERROR} from '../extensions/extensions/saga';
import {getExtensionProducts} from '../extensions/plan/saga';
import {
    generatePassword,
    generatePasswordBasedOnComplexityRules,
    getDefaultPasswordSetup,
} from '../../../utils/passwordGenerator';
import {TimeZone} from '../../types/TimeZone';

function generateAccountPassword(configData?: ConfigData): string {
    const minLength = parseInt(configData?.Accounts?.CreditAccMinServicePasswdLength ?? '8') || 8;
    const rules = configData?.Web?.PasswordComplexityRules;

    if (rules?.length) {
        return generatePasswordBasedOnComplexityRules(rules as PasswordComplexityRules[], minLength);
    }

    return generatePassword({ ...getDefaultPasswordSetup(), length: minLength });
}

export function* getAccountsList(
    action: ActionType<typeof actions.getAccountsList.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const {idFilter, emailFilter, cityFilter, billStatusFilter, limit, offset} = action.payload;

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams({
            has_extension: 0,
            get_only_real_accounts: 1,
            get_not_closed_accounts: 1,
            get_statuses: 1,
            get_total: 1,
            limit,
            offset,
            ...(idFilter ? {id: `%${idFilter}%`} : {}),
            ...(emailFilter ? {email: `%${emailFilter}%`} : {}),
            ...(cityFilter ? {city: `%${cityFilter}%`} : {}),
            ...(billStatusFilter ? {bill_status: billStatusFilter} : {}),
        });

        let res: AxiosResponse<{account_list: AccountListItem[]; total: number}>;
        let activeTotal: number;

        if (billStatusFilter) {
            res = yield call(api.post, Account.GetAccountList, body);
            activeTotal = billStatusFilter === 'O' ? res.data.total : 0;
        } else {
            const activeBody = new JSONFormData(session_id, csrf_token);
            activeBody.setParams({
                has_extension: 0,
                get_only_real_accounts: 1,
                get_not_closed_accounts: 1,
                get_total: 1,
                limit: 1,
                offset: 0,
                ...(idFilter ? {id: `%${idFilter}%`} : {}),
                ...(emailFilter ? {email: `%${emailFilter}%`} : {}),
                ...(cityFilter ? {city: `%${cityFilter}%`} : {}),
                bill_status: 'O',
            });

            const [mainRes, activeRes]: [
                AxiosResponse<{account_list: AccountListItem[]; total: number}>,
                AxiosResponse<{total: number}>,
            ] = yield all([
                call(api.post, Account.GetAccountList, body),
                call(api.post, Account.GetAccountList, activeBody),
            ]);

            res = mainRes;
            activeTotal = activeRes.data.total;
        }

        yield put(
            actions.getAccountsList.success({
                total: res.data.total,
                activeTotal,
                items: res.data.account_list,
                filters: {idFilter, emailFilter, cityFilter, billStatusFilter},
            }),
        );
    } catch (err) {
        yield put(actions.getAccountsList.failure(err as any));
        showToast(i18n.t<string>('errors:common.genericError'));
    }
}

export function* addAccount(
    action: ActionType<typeof actions.addAccount.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        let configData: ConfigData | undefined = yield select((state: ReduxState) => state.generic.configData);

        if (!configData) {
            yield getConfigData();
            configData = yield select((state: ReduxState) => state.generic.configData);
        }

        const h323_password = generateAccountPassword(configData);

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams({
            account_info: {
                billing_model: 1,
                h323_password,
                email: action.payload.email,
                i_product: action.payload.i_product,
                id: action.payload.accountId,
                ...(action.payload.activationDate ? {activation_date: action.payload.activationDate} : {}),
            },
        });

        yield api.post(Account.AddAccount, body);

        yield put(actions.addAccount.success());
        action.payload.successCallback?.();

        showToast(i18n.t<string>('screens:accounts.accountCreated'));

        history.push(getRoute(Routes.AccountDetails, {id: action.payload.accountId}));
    } catch (err: any) {
        const apiError: APIErrorInterface | undefined = err?.response?.data?.exception ?? err?.response?.data ?? err;
        yield put(actions.addAccount.failure(apiError as APIErrorInterface));

        if (isUnhandledAddAccountError(apiError?.faultcode ?? '', apiError?.faultstring ?? '')) {
            action.payload.errorCallback?.();
        }
    }
}

function isUnhandledAddAccountError(faultcode: string, faultstring: string): boolean {
    return !(
        isMaxOfferedQuantityFault(faultcode) ||
        faultcode === CreateAccountFaultCode.ServerAccountDuplicateId ||
        faultcode.includes('The ID') && faultcode.includes('account with the same id already exists') ||
        faultcode === CreateAccountFaultCode.ForbiddenCharacterId ||
        faultstring === CreateAccountFaultString.DuplicateId ||
        faultstring.toLowerCase().includes('duplicate')
    );
}

function isUnhandledEditAccountError(faultcode: string, faultstring: string): boolean {
    return !(
        faultcode === CreateAccountFaultCode.ServerAccountDuplicateId ||
        (faultcode.includes('The ID') && faultcode.includes('account with the same id already exists')) ||
        faultcode === CreateAccountFaultCode.ForbiddenCharacterId ||
        faultstring === CreateAccountFaultString.DuplicateId ||
        faultstring.toLowerCase().includes('duplicate')
    );
}

export function* getAccountDetails(
    action: ActionType<typeof actions.getAccountDetails.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);

        const listBody = new JSONFormData(session_id, csrf_token);
        listBody.setParams({id: action.payload.id, detailed_info: 1});

        const listRes: AxiosResponse<{account_list: AccountListItem[]; total: number}> = yield api.post(
            Account.GetAccountList,
            listBody,
        );

        const listAccount = listRes.data.account_list?.[0];
        if (!listAccount) {
            yield put(actions.getAccountDetails.failure({
                faultcode: EXTENSION_NOT_FOUND_BY_ID_API_ERROR,
                faultstring: i18n.t<string>('tooltips:extensions.extensionNotFound', {
                    value: action.payload.id,
                }),
            } as APIErrorInterface));
            return;
        }

        const infoBody = new JSONFormData(session_id, csrf_token);
        infoBody.setParams({i_account: listAccount.i_account, detailed_info: 1});

        const infoRes: AxiosResponse<{account_info: AccountListItem}> = yield api.post(
            Account.GetAccountInfo,
            infoBody,
        );

        const account: AccountListItem = {
            ...listAccount,
            ...infoRes.data.account_info,
        };

        yield call(getCountriesList);
        yield call(getTimeZonesList);

        if (account.country) {
            yield call(getSubdivisionsData, actions.getSubdivisionData.request({iso_3166_1_a2: account.country}));
        }

        yield call(getExtensionProducts, account.i_account);

        yield put(actions.getAccountDetails.success(account));
    } catch (err: any) {
        yield put(actions.getAccountDetails.failure(err?.response?.data ?? err));
    }
}

export function* editAccount(
    action: ActionType<typeof actions.editAccount.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const {i_account, initialValues, changedValues} = action.payload;

        const dataToSave = initialValues
            ? compareObjectsAndReturnDifferencesInValues(initialValues, changedValues)
            : changedValues;

        // Update general account fields (id, email, timezone, address, etc.)
        const generalFields: Record<string, any> = {};
        const generalKeys = ['id', 'email', 'time_zone_name', 'country', 'city', 'baddr1', 'zip', 'state', 'activation_date'];
        for (const key of generalKeys) {
            if (dataToSave[key] !== undefined) {
                generalFields[key] = dataToSave[key];
            }
        }

        if (generalFields.time_zone_name !== undefined) {
            const timezones: TimeZone[] = yield select(
                (state: ReduxState) => state.generic.timeZonesList ?? [],
            );
            const timezoneId = timezones.find(
                (v) => v.time_zone_name === generalFields.time_zone_name,
            )?.i_time_zone;

            if (timezoneId) {
                generalFields.i_time_zone = timezoneId;
            }
            delete generalFields.time_zone_name;
        }

        if (Object.keys(generalFields).length > 0) {
            const body = new JSONFormData(session_id, csrf_token);
            body.setParams({
                account_info: {
                    i_account,
                    ...generalFields,
                },
            });
            yield api.post(Account.UpdateAccount, body);
        }

        // Update plan (product + addons)
        if (dataToSave.selectedMainProduct !== undefined || dataToSave.selectedAddonsProducts !== undefined) {
            const selectedMainProduct = dataToSave.selectedMainProduct ?? changedValues.selectedMainProduct ?? initialValues?.selectedMainProduct;
            const selectedAddonsProducts = dataToSave.selectedAddonsProducts ?? changedValues.selectedAddonsProducts ?? initialValues?.selectedAddonsProducts ?? [];

            const planBody = new JSONFormData(session_id, csrf_token);
            planBody.setParams({
                account_info: {
                    i_account,
                    i_product: selectedMainProduct,
                    assigned_addons: selectedAddonsProducts.map((v: number) => ({
                        i_product: v,
                    })),
                },
            });
            yield api.post(Account.UpdateAccount, planBody);
        }

        showToast(i18n.t<string>('screens:accounts.accountEdited'));

        const newId = changedValues.id;
        const oldId = initialValues?.id;
        if (newId && oldId && newId !== oldId) {
            yield delay(2500);
            const tabParam = qs.parse(location.search, {ignoreQueryPrefix: true}).tab;
            const search = tabParam !== undefined ? `?${qs.stringify({tab: tabParam})}` : '';
            yield put(actions.editAccount.success());
            location?.replace(`${getRoute(Routes.AccountDetails, {id: newId})}${search}`);
        } else {
            yield put(actions.editAccount.success());
            action.payload.successCallback?.();
        }

    } catch (err: any) {
        const apiError: APIErrorInterface | undefined = err?.response?.data?.exception ?? err?.response?.data ?? err;
        yield put(actions.editAccount.failure(apiError as APIErrorInterface));

        if (isUnhandledEditAccountError(apiError?.faultcode ?? '', apiError?.faultstring ?? '')) {
            showToast(
                apiError?.faultstring
                    || i18n.t<string>('errors:common.genericError'),
            );
        }
    }
}

export function* getAccountTransactions(
    action: ActionType<typeof actions.getAccountTransactions.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams({
            i_account: action.payload.i_account,
            from_date: action.payload.fromDate,
            to_date: action.payload.toDate,
            get_total: 1,
            limit: action.payload.limit,
            offset: action.payload.offset,
            ...(action.payload.with_netaccess_usage ? {with_netaccess_usage: action.payload.with_netaccess_usage} : {}),
        });

        const res: AxiosResponse<{xdr_list: TransactionItem[]; total: number}> = yield api.post(
            Account.GetXdrList,
            body,
        );

        yield put(
            actions.getAccountTransactions.success({
                total: res.data.total,
                items: res.data.xdr_list ?? [],
            }),
        );
    } catch (err) {
        yield put(actions.getAccountTransactions.failure(err as any));
        showToast(i18n.t<string>('errors:common.genericError'));
    }
}

export const accountsSaga = [
    takeLatest(actions.getAccountsList.request, getAccountsList),
    takeLatest(actions.addAccount.request, addAccount),
    takeLatest(actions.getAccountDetails.request, getAccountDetails),
    takeLatest(actions.editAccount.request, editAccount),
    takeLatest(actions.getAccountTransactions.request, getAccountTransactions),
];
